/**
 * Contrato de mensajes del puente builder (app del cliente ↔ dashboard).
 * Ambos lados validan `event.origin` y que `token` coincida antes de actuar.
 *
 * Flujo:
 *   1. El dashboard abre la app con `?veoBuilder=<token>&veoBuilderOrigin=<origen>`.
 *   2. La app (veo-builder.js) postea `ready` a la ventana que la abrió.
 *   3. El dashboard manda comandos (`start-picker`, `preview`, …).
 *   4. La app responde con eventos (`picked`, `pick-cancelled`, …).
 */
import type { PreviewGuideInput } from '../guides/guide-preview';
import type { BlockDropPayload } from './block-drop-target';
import type { DesignPatch } from './design-manipulator';
import type { PickedElement } from './element-picker';

export const VEO_BUILDER_SOURCE = 'veo-builder';
// Los nombres de los query params viven en el core (`builder-token.ts`), única
// fuente de verdad compartida con el loader y el gate del runtime de guías.
export { BUILDER_ORIGIN_PARAM, BUILDER_TOKEN_PARAM } from '../../core/builder-token';

/** Eventos que la app del cliente envía al dashboard. */
export type BuilderEvent =
  | { type: 'ready' }
  | { type: 'picked'; payload: PickedElement }
  | { type: 'pick-cancelled' }
  // Manipulación directa: el autor arrastró/redimensionó la guía en la app;
  // el panel mergea `payload.style` sobre el `style` del paso indicado.
  | { type: 'design-updated'; payload: DesignPatch }
  // Drag & drop de bloques: el autor soltó un bloque de la paleta SOBRE la
  // guía en preview; el panel lo inserta en `step.blocks[]` en esa posición.
  | { type: 'block-dropped'; payload: BlockDropPayload }
  | { type: 'closed' };

/** Comandos que el dashboard/panel envía a la app del cliente. */
export type BuilderCommand =
  | { type: 'start-picker'; maxAncestors?: number; hint?: string }
  | { type: 'stop-picker' }
  // `editable: true` (editor in-app) adjunta drag/resize sobre la preview.
  | { type: 'preview'; guide: PreviewGuideInput; editable?: boolean }
  // Drag de un bloque de la paleta POR POINTER (el DnD HTML5 no cruza bien el
  // iframe del panel): el panel captura el puntero y streamea sus coordenadas
  // RELATIVAS AL IFRAME; la app las convierte con el rect del panel, mueve la
  // línea de inserción y en `drop` responde con el evento `block-dropped`.
  | {
      type: 'block-drag';
      phase: 'move' | 'drop' | 'cancel';
      x: number;
      y: number;
      blockType: string;
      label?: string;
    }
  | { type: 'close-preview' }
  | { type: 'teardown' }
  // Modo panel inyectado: el panel avisa que cargó y está listo para recibir
  // eventos; la app responde con `ready`. Resuelve la carrera de que el iframe
  // del panel todavía no tenga su listener cuando la app postea `ready`.
  | { type: 'panel-ready' };

/** Sobre común: todo mensaje del puente lleva `source` + `token`. */
export interface BuilderEnvelope {
  source: typeof VEO_BUILDER_SOURCE;
  token: string;
}

export type BuilderEventMessage = BuilderEnvelope & BuilderEvent;
export type BuilderCommandMessage = BuilderEnvelope & BuilderCommand;
